import { DynamicObjectInterface } from '../../sdk/type';

// MY MULTI 5-step progress model. Step 3 (Check-In) is configurable per sub-challenge:
// when no check-in days are configured it stays a grey placeholder (complete: null) and
// progress behaves as before; when days are configured it becomes a real step that is
// complete only once every configured day has been submitted.
export enum TeamProgressStep {
  PURCHASE = 'PURCHASE',
  PRE_ASSESSMENT = 'PRE_ASSESSMENT',
  CHECK_IN = 'CHECK_IN',
  POST_ASSESSMENT = 'POST_ASSESSMENT',
  RESULT = 'RESULT',
}

export const TEAM_PROGRESS_TOTAL_STEPS = 5;

export interface TeamProgressStepState {
  step: TeamProgressStep;
  // null = not yet evaluated/grey placeholder, true = passed (green), false = not done
  complete: boolean | null;
  // Overall Results only: the step is reached/done but the winning criteria were not
  // met — rendered red (cross / red segment). Distinct from `complete === false`,
  // which just means "not reached yet" (grey) for the other steps.
  failed?: boolean;
}

export interface TeamProgress {
  isAllPurchase: boolean;
  isAllValidPurchase: boolean;
  isAllWeightIn: boolean;
  isAllWeightOut: boolean;
  isAllWin: boolean;
  // null = check-in not configured (grey placeholder); otherwise whether it is fully done.
  isAllCheckIn: boolean | null;
  steps: TeamProgressStepState[];
  completedSteps: number;
  totalSteps: number;
  // i18n key for the "Next up" label, or null when the journey is finished.
  nextStepKey: string | null;
}

const isActive = (participant: DynamicObjectInterface): boolean => participant?.status === 'ACTIVE';

const countActiveWhere = (
  participants: DynamicObjectInterface[],
  predicate: (p: DynamicObjectInterface) => boolean,
): number => participants.filter(p => isActive(p) && predicate(p)).length;

// Normalise configured check-in days to a clean, unique list of positive day numbers.
const normaliseCheckInDays = (checkInDays?: (number | string)[]): number[] =>
  Array.from(new Set((checkInDays ?? []).map(d => Number(d)).filter(d => !isNaN(d) && d > 0)));

// A member has completed check-in when they have a submission for every configured day.
const memberHasAllCheckIns = (member: DynamicObjectInterface, days: number[]): boolean => {
  if (days.length === 0) return false;
  const submittedDays = new Set(
    (Array.isArray(member?.checkIns) ? member.checkIns : [])
      .map((c: DynamicObjectInterface) => Number(c?.day))
      .filter((d: number) => !isNaN(d)),
  );
  return days.every(d => submittedDays.has(d));
};

// Build the shared step list + derived fields. checkInComplete is null when check-in
// is not configured, keeping the grey placeholder behaviour for non-check-in campaigns.
const buildProgress = (
  flags: {
    isAllPurchase: boolean;
    isAllValidPurchase: boolean;
    isAllWeightIn: boolean;
    isAllWeightOut: boolean;
    isAllWin: boolean;
  },
  checkInComplete: boolean | null,
): TeamProgress => {
  const { isAllPurchase, isAllValidPurchase, isAllWeightIn, isAllWeightOut, isAllWin } = flags;

  const steps: TeamProgressStepState[] = [
    { step: TeamProgressStep.PURCHASE, complete: isAllPurchase },
    { step: TeamProgressStep.PRE_ASSESSMENT, complete: isAllWeightIn },
    { step: TeamProgressStep.CHECK_IN, complete: checkInComplete },
    { step: TeamProgressStep.POST_ASSESSMENT, complete: isAllWeightOut },
    // Overall Results = journey done once Post-Assessment is submitted. `failed` flags
    // a completed-but-not-won result (red). complete is false (not null) when not done,
    // so at campaign-end it renders red like the other incomplete steps; before end it
    // still shows grey (segmentClass treats complete=false as grey unless ended).
    {
      step: TeamProgressStep.RESULT,
      complete: isAllWeightOut ? true : false,
      failed: isAllWeightOut && !isAllWin,
    },
  ];

  const completedSteps = steps.filter(s => s.complete === true).length;

  // Next-up ordering: Purchase -> Pre -> Check-In (only when configured) -> Post.
  let nextStepKey: string | null = null;
  if (!isAllPurchase) {
    nextStepKey = 'dashboard.home.label.nextStepTeamMemberRegistration';
  } else if (!isAllWeightIn) {
    nextStepKey = 'dashboard.home.label.nextStepPreAssessment';
  } else if (checkInComplete === false) {
    nextStepKey = 'dashboard.home.label.nextStepCheckIn';
  } else if (!isAllWeightOut) {
    nextStepKey = 'dashboard.home.label.nextStepPostAssessment';
  } else {
    nextStepKey = null;
  }

  return {
    isAllPurchase,
    isAllValidPurchase,
    isAllWeightIn,
    isAllWeightOut,
    isAllWin,
    isAllCheckIn: checkInComplete,
    steps,
    completedSteps,
    totalSteps: TEAM_PROGRESS_TOTAL_STEPS,
    nextStepKey,
  };
};

export const computeTeamProgress = (
  team: DynamicObjectInterface,
  checkInDays?: (number | string)[],
): TeamProgress => {
  const participants: DynamicObjectInterface[] = Array.isArray(team?.participants) ? team.participants : [];
  const rawTeamSize = team?.participationType === 'ind' ? '1' : team?.teamSize;
  const teamSize = parseInt(rawTeamSize ?? '0', 10) || 0;

  const meets = (count: number) => teamSize > 0 && count >= teamSize;

  const isAllPurchase = meets(countActiveWhere(participants, p => p?.purchase?.length > 0));
  const isAllValidPurchase = meets(countActiveWhere(participants, p => p?.purchase?.[0]?.orderStatus === 1));
  const isAllWeightIn = meets(countActiveWhere(participants, p => !!p?.weightIn?.weightInDate));
  const isAllWeightOut = meets(countActiveWhere(participants, p => !!p?.weightOut?.weightOutDate));
  const isAllWin = meets(countActiveWhere(participants, p => !!p?.isIndividualAchieved));

  const days = normaliseCheckInDays(checkInDays);
  // Team-level: every active member must have submitted every configured check-in day.
  const checkInComplete = days.length === 0 ? null : meets(countActiveWhere(participants, p => memberHasAllCheckIns(p, days)));

  return buildProgress({ isAllPurchase, isAllValidPurchase, isAllWeightIn, isAllWeightOut, isAllWin }, checkInComplete);
};

// "Locked" = team has not started yet (no teamStartDate). No DB flag backs this.
export const isMemberLocked = (team: DynamicObjectInterface): boolean => !team?.teamStartDate;

// Per-member 5-step progress, mirroring the team model but evaluated against a
// single participant row.
export const computeMemberProgress = (
  member: DynamicObjectInterface,
  checkInDays?: (number | string)[],
): TeamProgress => {
  const hasPurchase = (member?.purchase?.length ?? 0) > 0;
  const hasValidPurchase = member?.purchase?.[0]?.orderStatus === 1;
  const hasWeightIn = !!member?.weightIn?.weightInDate;
  const hasWeightOut = !!member?.weightOut?.weightOutDate;
  const hasWin = !!member?.isIndividualAchieved;

  const days = normaliseCheckInDays(checkInDays);
  const checkInComplete = days.length === 0 ? null : memberHasAllCheckIns(member, days);

  return buildProgress(
    {
      isAllPurchase: hasPurchase,
      isAllValidPurchase: hasValidPurchase,
      isAllWeightIn: hasWeightIn,
      isAllWeightOut: hasWeightOut,
      isAllWin: hasWin,
    },
    checkInComplete,
  );
};
